import { ReactNode, ButtonHTMLAttributes } from 'react'
import { twMerge } from 'tailwind-merge'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-light-svg-icons'

type ButtonColor = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'fancy'
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  children?: ReactNode
  color?: ButtonColor
  size?: ButtonSize
  icon?: IconDefinition
  iconPosition?: 'left' | 'right'
  loading?: boolean
  fullWidth?: boolean
  outline?: boolean
  onDark?: boolean
}

const colorStyles: Record<ButtonColor, { solid: string; outline: string; solidDark: string; outlineDark: string }> = {
  primary: {
    solid:
      'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 shadow-sm shadow-indigo-200 focus:ring-indigo-500',
    outline: 'border-indigo-300 text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 focus:ring-indigo-500',
    solidDark: 'bg-indigo-600 text-white hover:bg-indigo-500 active:bg-indigo-700 focus:ring-indigo-400',
    outlineDark:
      'border-indigo-400 text-indigo-300 hover:bg-indigo-500/20 active:bg-indigo-500/30 focus:ring-indigo-400'
  },
  secondary: {
    solid:
      'bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 border border-slate-300 focus:ring-slate-400',
    outline: 'border-slate-400 text-slate-600 hover:bg-slate-50 active:bg-slate-100 focus:ring-slate-400',
    solidDark: 'bg-white/10 text-white hover:bg-white/20 active:bg-white/25 focus:ring-white/50',
    outlineDark: 'border-white/40 text-white hover:bg-white/10 active:bg-white/20 focus:ring-white/50'
  },
  success: {
    solid:
      'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm shadow-emerald-200 focus:ring-emerald-500',
    outline: 'border-emerald-300 text-emerald-600 hover:bg-emerald-50 active:bg-emerald-100 focus:ring-emerald-500',
    solidDark: 'bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700 focus:ring-emerald-400',
    outlineDark:
      'border-emerald-400 text-emerald-300 hover:bg-emerald-500/20 active:bg-emerald-500/30 focus:ring-emerald-400'
  },
  danger: {
    solid: 'bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 shadow-sm shadow-rose-200 focus:ring-rose-500',
    outline: 'border-rose-300 text-rose-600 hover:bg-rose-50 active:bg-rose-100 focus:ring-rose-500',
    solidDark: 'bg-rose-600 text-white hover:bg-rose-500 active:bg-rose-700 focus:ring-rose-400',
    outlineDark: 'border-rose-400 text-rose-300 hover:bg-rose-500/20 active:bg-rose-500/30 focus:ring-rose-400'
  },
  warning: {
    solid:
      'bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700 shadow-sm shadow-amber-200 focus:ring-amber-500',
    outline: 'border-amber-300 text-amber-600 hover:bg-amber-50 active:bg-amber-100 focus:ring-amber-500',
    solidDark: 'bg-amber-500 text-white hover:bg-amber-400 active:bg-amber-600 focus:ring-amber-400',
    outlineDark: 'border-amber-400 text-amber-300 hover:bg-amber-500/20 active:bg-amber-500/30 focus:ring-amber-400'
  },
  ghost: {
    solid: 'bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200 focus:ring-slate-400',
    outline: 'border-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200 focus:ring-slate-400',
    solidDark: 'bg-transparent text-white hover:bg-white/10 active:bg-white/20 focus:ring-white/50',
    outlineDark: 'border-transparent text-white hover:bg-white/10 active:bg-white/20 focus:ring-white/50'
  },
  fancy: {
    solid:
      'bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 active:from-indigo-800 active:to-violet-800 shadow-md shadow-indigo-200 focus:ring-indigo-500',
    outline: 'border-indigo-300 text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 focus:ring-indigo-500',
    solidDark: 'bg-white text-slate-900 hover:bg-slate-100 active:bg-slate-200 font-semibold focus:ring-white/50',
    outlineDark: 'border-white/50 text-white hover:bg-white/10 active:bg-white/20 focus:ring-white/50'
  }
}

const sizeStyles: Record<ButtonSize, string> = {
  xs: 'px-2.5 py-1 text-xs gap-1.5 rounded-md',
  sm: 'px-3 py-1.5 text-sm gap-1.5 rounded-lg',
  md: 'px-4 py-2 text-sm gap-2 rounded-lg',
  lg: 'px-5 py-2.5 text-base gap-2 rounded-xl'
}

const iconSizeStyles: Record<ButtonSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-base'
}

export const Button = ({
  children,
  color = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  outline = false,
  onDark = false,
  disabled,
  className,
  ...props
}: ButtonProps) => {
  const isDisabled = disabled || loading

  // Select appropriate color style based on outline and onDark props
  let colorStyle: string
  if (onDark) {
    colorStyle = outline ? colorStyles[color].outlineDark : colorStyles[color].solidDark
  } else {
    colorStyle = outline ? colorStyles[color].outline : colorStyles[color].solid
  }

  return (
    <button
      disabled={isDisabled}
      className={twMerge(
        // Base styles
        'inline-flex items-center justify-center font-medium transition-all duration-150',
        'focus:outline-none focus:ring-2',
        // Ring offset - transparent on dark backgrounds
        onDark ? 'focus:ring-offset-0' : 'focus:ring-offset-2',
        // Size
        sizeStyles[size],
        // Color
        colorStyle,
        // Outline border
        outline && 'border-2 bg-transparent',
        // Full width
        fullWidth && 'w-full',
        // Disabled/Loading state
        isDisabled && 'opacity-60 cursor-not-allowed pointer-events-none',
        // Custom classes
        className
      )}
      {...props}
    >
      {/* Loading Spinner */}
      {loading && <FontAwesomeIcon icon={faSpinner} className={twMerge('animate-spin', iconSizeStyles[size])} />}

      {/* Left Icon */}
      {!loading && icon && iconPosition === 'left' && <FontAwesomeIcon icon={icon} className={iconSizeStyles[size]} />}

      {/* Children */}
      {children && <span>{children}</span>}

      {/* Right Icon */}
      {!loading && icon && iconPosition === 'right' && <FontAwesomeIcon icon={icon} className={iconSizeStyles[size]} />}
    </button>
  )
}

// Icon-only button variant
interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  icon: IconDefinition
  color?: ButtonColor
  size?: ButtonSize
  loading?: boolean
  'aria-label': string
}

export const IconButton = ({
  icon,
  color = 'ghost',
  size = 'md',
  loading = false,
  disabled,
  className,
  ...props
}: IconButtonProps) => {
  const isDisabled = disabled || loading
  const colorStyle = colorStyles[color].solid

  const sizeMap: Record<ButtonSize, string> = {
    xs: 'w-7 h-7 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-9 h-9 text-sm',
    lg: 'w-11 h-11 text-base'
  }

  const radiusMap: Record<ButtonSize, string> = {
    xs: 'rounded-md',
    sm: 'rounded-lg',
    md: 'rounded-lg',
    lg: 'rounded-xl'
  }

  return (
    <button
      disabled={isDisabled}
      className={twMerge(
        'inline-flex items-center justify-center transition-all duration-150',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        sizeMap[size],
        radiusMap[size],
        colorStyle,
        isDisabled && 'opacity-60 cursor-not-allowed pointer-events-none',
        className
      )}
      {...props}
    >
      {loading ? <FontAwesomeIcon icon={faSpinner} className='animate-spin' /> : <FontAwesomeIcon icon={icon} />}
    </button>
  )
}

// Button Group
interface ButtonGroupProps {
  children: ReactNode
  className?: string
}

export const ButtonGroup = ({ children, className }: ButtonGroupProps) => {
  return (
    <div
      className={twMerge(
        'inline-flex rounded-lg overflow-hidden',
        '[&>button]:rounded-none [&>button:first-child]:rounded-l-lg [&>button:last-child]:rounded-r-lg',
        '[&>button:not(:last-child)]:border-r [&>button:not(:last-child)]:border-r-black/10',
        className
      )}
    >
      {children}
    </div>
  )
}

export default Button
